import {
  getAppCustomEnv,
  GetCompaniesByNameProxy,
  renderSnackbarMessage,
  growthbookUtil,
} from "@sleek/customer-common-acquisition";
import axios, { CancelToken } from "axios";
import vueStore from "@/store";
import { Subject, Observable, from } from "rxjs";
import get from "lodash/get";
import Cookies from "js-cookie";
import { OrderProxy } from "@/proxies/order/order.proxy";
import { CrmService } from "customer-sdk/crm";
import { debounceTime, switchMap, exhaustMap } from "rxjs/operators";

import { evalCondition } from "@/modules/sleek-onboarding/utils/rule-evaluator.js";
import { omit } from "lodash";

const getCartObservableInput = new Subject();
getCartObservableInput
  .pipe(
    debounceTime(200),
    switchMap((requestPayload) => {
      const { bundleId, onboardingData, orderId } = requestPayload;
      return cartRequestObservable(bundleId, onboardingData, orderId);
    })
    // catchError(() => Observable.of({}))
    // tap((res) => console.log("tap", res))
  )
  .subscribe(() => {});
const cartRequestObservable = (bundleId, onboardingData, orderId = null) =>
  Observable.create(function (observer) {
    const cancelSource = CancelToken.source();
    if (orderId) {
      onboardingData.orderId = orderId;
    }
    getAppCustomEnv()
      .then((appCustomEnv) => {
        const { VUE_APP_PLATFORM_ACQUISITION_API } = appCustomEnv;
        const endpointUrl = "bundle";
        vueStore.commit("onboardingFlowModule/setCartLoading", true);
        return axios.post(
          `${VUE_APP_PLATFORM_ACQUISITION_API}/${endpointUrl}/${bundleId}`,
          onboardingData,
          {
            cancelToken: cancelSource.token,
          }
        );
      })
      .then(
        (result) => {
          const { data } = result;
          if (data) {
            vueStore.commit("onboardingFlowModule/setCartData", data);

            vueStore.commit(
              "onboardingFlowModule/setCouponArray",
              data.couponArray
            );
            vueStore.commit(
              "onboardingFlowModule/setCouponEligible",
              data.coupon
            );

            observer.next(data);
          }
          vueStore.commit("onboardingFlowModule/setCartLoading", false);
          observer.complete();
        },
        (err) => {
          if (axios.isCancel(err)) {
            observer.complete();
          } else {
            console.error("Error while gettingCart data", err);
            observer.error(err);
          }
        }
      );

    return () => cancelSource.cancel();
  });

const initialState = {
  bundleId: null,
  order: null,
  pageServices: [],
  flowServices: [],
  selectedOptionalFlowServices: [],
  isSetOptionalInclusion: false,
  currentPage: {},
  isPackageMobile: true,
  // Form
  // Form--CompanyName
  companyName: "",
  suffix: "LTD",
  desiredCompanyName: [],
  // Form--Director
  directors: [],
  // Form--CompanySecretary,
  hasCompanySecretary: true,
  secretaries: [],
  // Form--Shares,
  shares: {},
  // Form--Add Ons
  addOnsSelected: [],
  // Form--Mailroom
  hasMailroom: true,
  ownAddress: {
    address1: "",
    address2: "",
    address3: "",
    state: "",
    // city: "",
    postalCode: "",
    postTown: "",
    houseName: "",
    country: "",
  },
  // Form--Accounting
  needAccounting: true,
  accountingSubscriptionType: "", // 2_500_expense_annual_filling_bookkeeping_plan or internalName
  accountingExpense: 2500, // Undefined = not selected , null = more than 75k
  accountingTier: "accounting-annual-filling",
  accountingAddons: [],
  accountingAddonsAdditionalData: [],
  validateAccountingAddons: {},
  inputtedAverageMonthlyExpense: null, // For more than the selectable accounting monthly expenses
  annualUpfrontSelected: false,
  isSelectedAccountingDropdownOption: false,
  // Form--BusinessAccount
  businessAccountServicesIncluded: [],
  // End of form
  cartData: undefined,
  isCartLoading: false,
  paymentType: 1,
  isAllowSleekToStayInTouch: false,
  contactDetails: {},
  pointOfContact: {},
  coupon: "",
  couponArray: [],
  companyCreatedId: null,
  invoice: {},
  checkoutItems: [],
  // Save for later
  saveForLater: {},
  needGuidancePopup: false,
  isSetInitialValues: false,
  popupTimeout: "",
  showComparePlanPopup: false,
  packageType: "",
  isMandatoryDeclarationChecked: null,
  isTransferAccountingUK: false,
  isAgreeTerms: false,
  collapsedColC: true,
  questionnaireAnswerList: {},
  isCompletedQuestionnaire: false,
  couponEligible: "",
  isLoadingOrder: false,
  isCheckoutPage: false,
  isBilledYearly: false,
};

const getters = {
  // general
  getPageServices: (state) => state.pageServices,
  getFlowServices: (state) => state.flowServices,
  getSelectedOptionalFlowServices: (state) =>
    state.selectedOptionalFlowServices,
  isOptionalFlowServiceSelected: (state) => (internalName) => {
    return state.selectedOptionalFlowServices.indexOf(internalName) > -1;
  },
  getBundleId: (state) => state.bundleId,
  getCurrentPage: (state) => state.currentPage,
  getCartData: (state) => state.cartData,
  isCartLoading: (state) => state.isCartLoading,
  getNextPage: (state) => {
    const currentPageIndex = state.pageServices.findIndex(
      (page) => page.route === state.currentPage.route
    );

    let jumpIndex = 1;
    let nextPage = state.pageServices[currentPageIndex + jumpIndex];

    if (!nextPage) {
      return null;
    }

    while (nextPage.skipCondition) {
      const result = evalCondition(state, nextPage.skipCondition);
      if (result) {
        jumpIndex++;
        const pageAfterSkip = state.pageServices[currentPageIndex + jumpIndex];
        if (pageAfterSkip) {
          nextPage = pageAfterSkip;
        }
      } else {
        return nextPage;
      }
    }
    return nextPage;
  },
  getFirstPage: (state, getters) => {
    const firstPage = state.pageServices[0];
    if (
      firstPage.skipCondition &&
      evalCondition(state, firstPage.skipCondition)
    ) {
      return getters.getNextPage;
    }
    return firstPage;
  },
  getPreviousPage: (state) => {
    const currentPageIndex = state.pageServices?.findIndex(
      (page) => page.route === state.currentPage.route
    );

    let jumpIndex = 1;
    let previousPage = state.pageServices[currentPageIndex - jumpIndex];

    if (!previousPage) {
      return null;
    }

    while (previousPage.skipCondition) {
      const result = evalCondition(state, previousPage.skipCondition);
      if (result) {
        jumpIndex++;
        const pageAfterSkip = state.pageServices[currentPageIndex - jumpIndex];
        if (pageAfterSkip) {
          previousPage = pageAfterSkip;
        }
      } else {
        return previousPage;
      }
    }
    return previousPage;
  },
  getCurrentPageIndex: (state) =>
    state.pageServices.findIndex(
      (page) => page.route === state.currentPage.route
    ),
  // End general
  getSelectedServices: (state) => {
    if (!state.cartData) return [];
    const services = [
      ...(state.cartData.fulfilledServices || []),
      ...(state.cartData.purchasedFromPageServices || []),
      ...(state.cartData.addOnsResult || []),
      ...(state.cartData.businessAccountResult || []),
    ].filter((x) => (x.optional ? x.selected : true));
    return services;
  },
  getIsCurrentPageValid: (state) => {
    if (!state.currentPage) return true;
    return true;
  },
  getOrder: (state) => state.order,
  // company name
  getCompanyName: (state) => state.companyName,
  getSuffix: (state) => state.suffix,
  getDesiredCompanyName: (state) => state.desiredCompanyName,
  // company secretary
  getHasCompanySecretary: (state) => state.hasCompanySecretary,
  getSecretaries: (state) => state.secretaries,
  // shares
  getShares: (state) => state.shares,
  // add ons
  getAddOnsSelected: (state) => state.addOnsSelected,
  // mailroom
  getHasMailroom: (state) => state.hasMailroom,
  // accounting
  getAccountingSubscriptionType: (state) => state.accountingSubscriptionType,
  getAccountingAddons: (state) => state.accountingAddons,
  getAccountingAddonsAdditionalData: (state) =>
    state.accountingAddonsAdditionalData,
  getValidateAccountingAddons: (state) => state.validateAccountingAddons,
  getAccountingExpense: (state) => state.accountingExpense,
  getAccountingTier: (state) => state.accountingTier,
  getNeedAccounting: (state) => state.needAccounting,
  getInputtedAverageMonthlyExpense: (state) =>
    state.inputtedAverageMonthlyExpense,
  // business account
  getBusinessAccountServicesIncluded: (state) =>
    state.businessAccountServicesIncluded,
  // payment
  getPaymentType: (state) => state.paymentType,
  getIsAllowSleekToStayInTouch: (state) => state.isAllowSleekToStayInTouch,
  getContactDetails: (state) => state.contactDetails,
  getPointOfContact: (state) => state.pointOfContact,
  getDirectors: (state) => state.directors,
  getCoupon: (state) => state.coupon,
  getCouponArray: (state) => state.couponArray,
  getCompanyCreatedId: (state) => state.companyCreatedId,
  getInvoice: (state) => state.invoice,
  // checkout
  getCheckoutItems: (state) => state.checkoutItems,
  // save for later
  getSaveForLater: (state) => state.saveForLater,
  isAccountingFormValid: (state) => {
    const pageServices = state.pageServices;
    const accountingPageService = pageServices.find((service) => {
      return service.route.includes("accounting-uk");
    });
    if (!accountingPageService) return true;

    // for accounting logic page
    if (typeof state.accountingExpense === "undefined") return false;
    if (state.needAccounting === false) return false;
    if (state.accountingExpense === null) {
      if (!state.inputtedAverageMonthlyExpense) return false;
      if (Number.isNaN(state.inputtedAverageMonthlyExpense)) return false;

      const { uiConfiguration } = accountingPageService;
      const maximumSelectableExpense = get(
        uiConfiguration,
        "maximumSelectableExpense",
        200000
      );

      return (
        Number(state.inputtedAverageMonthlyExpense) > maximumSelectableExpense
      );
    }
    if (state.accountingAddons?.length) {
      for (const internalName of state.accountingAddons) {
        if (state.validateAccountingAddons[internalName] === false)
          return false;
      }
    }
    return !!state.accountingTier;
  },
  getAnnualUpfrontSelected: (state) => state.annualUpfrontSelected,
  getIsSelectedAccountingDropdownOption: (state) =>
    state.isSelectedAccountingDropdownOption,
  getNeedGuidancePopup: (state) => state.needGuidancePopup,
  getPopupTimeout: (state) => state.popupTimeout,
  setIsSetInitialValues: (state) => state.isSetInitialValues,
  getCurrentState: (state) => state,
  getShowComparePlanPopup: (state) => state.showComparePlanPopup,
  // Mailroom Own Address
  getOwnAddress: (state) => state.ownAddress,
  getIsMandatoryDeclarationChecked: (state) =>
    state.isMandatoryDeclarationChecked,
  isTransfer: (state, getters) => {
    const currentCheckoutItems = getters.getCheckoutItems;
    if (!currentCheckoutItems) return false;
    // Negate, if checkoutItems has one "company_house_fee". it's Incorporation
    return !currentCheckoutItems.some((service) =>
      service.internalName.includes("company_house_fee")
    );
  },
  getIsAgreeTerms: (state) => state.isAgreeTerms,
  getCollapsedColC: (state) => state.collapsedColC,
  getQuestionnaireAnswerList: (state) => state.questionnaireAnswerList,
  getCouponEligible: (state) => state.couponEligible,
  getIsLoadingOrder: (state) => state.isLoadingOrder,
  getIsCheckoutPage: (state) => state.isCheckoutPage,
  getPackageType: (state) => state.packageType,
  getIsBilledYearly: (state) => state.isBilledYearly,
};

const validateCompanyName = async (state, payload) => {
  try {
    const getCompaniesByNameProxy = new GetCompaniesByNameProxy();
    const { isExisting } = await getCompaniesByNameProxy.validateCompanyName({
      name: `${payload.companyName} ${payload.companyPrefix}` || "",
    });

    return isExisting;
  } catch (error) {
    // Todo setup general.messages.errors
    renderSnackbarMessage(
      state.platformConfig.general.messages.errors.GENERAL,
      6000
    );
  }
};

const actions = {
  async processBeforePaymentAction({ state }) {
    const proxy = new OrderProxy();
    const {
      companyName,
      suffix,
      contactDetails,
      pointOfContact,
      directors,
      secretaries,
      shares,
      checkoutItems,
      order,
      ownAddress,
      packageType,
    } = state;
    const data = {
      ...order,
      companyName:
        companyName && companyName.length
          ? companyName
          : pointOfContact &&
            pointOfContact.email &&
            pointOfContact.email.length
          ? `Draft Company ${new Date().toDateString()}`
          : null,
      companyNameSuffix: suffix,
      userContactInfo: contactDetails,
      pointOfContact,
      shares,
      secretaries,
      directors,
      checkoutItems,
      ownAddress,
      packageType,
      orderId: order._id,
    };
    await proxy.processBeforePayment(data);
  },
  async updateOrderAction({ state, dispatch }) {
    const updateOrderSubject = new Subject();
    const proxy = new OrderProxy();
    const {
      companyName,
      suffix,
      contactDetails,
      pointOfContact,
      directors,
      secretaries,
      shares,
      checkoutItems,
      order,
      ownAddress,
      packageType,
      annualUpfrontSelected,
    } = state;
    const data = {
      ...order,
      companyName:
        companyName && companyName.length
          ? companyName
          : pointOfContact &&
            pointOfContact.email &&
            pointOfContact.email.length
          ? `Draft Company ${new Date().toDateString()}`
          : null,
      companyNameSuffix: suffix,
      userContactInfo: contactDetails,
      pointOfContact,
      shares,
      secretaries,
      directors,
      checkoutItems,
      ownAddress,
      packageType,
      interestedUpfront: annualUpfrontSelected,
    };
    const crmService = new CrmService();
    data.hutk = crmService.getCrmUserToken();

    updateOrderSubject
      .pipe(
        exhaustMap(() =>
          from(
            (async () => {
              try {
                state.isLoadingOrder = true;
                let response;
                if (order && order._id) {
                  const resUpdateOrder = await proxy.updateOrder(
                    order._id,
                    data
                  );
                  response = resUpdateOrder;
                  if (resUpdateOrder?.data?.doneOrder) {
                    dispatch("resetOrderAction");
                    dispatch("clearOnboardingData");
                    return data;
                  }
                } else {
                  response = await proxy.createOrder(data);
                }

                if (response) {
                  dispatch("setOrderAction", response.data);
                  Cookies.set("orderId", response.data._id, {
                    expires: 7,
                    sameSite: "strict",
                    secure: true,
                  });
                }
                return data;
              } catch (error) {
                if (
                  error.message &&
                  error.message.includes("Your session has been done")
                ) {
                  const incorpShorterValue = growthbookUtil.getFeatureValue(
                    "sa-37518_shorter_incorporation_flow",
                    {
                      isActive: false,
                      listBundle: [],
                    }
                  );
                  const { isActive: isActiveShorter, listBundle } = incorpShorterValue;
                  if (
                    isActiveShorter &&
                    listBundle.includes(this.getBundleId)
                  ) {
                    this.$router.push({ name: "thank-you-uk" });
                  } else {
                    localStorage.clear();
                    dispatch("resetOrderAction");
                    dispatch("clearOnboardingData");
                    const firstPage = getters.getFirstPage(state, getters);
                    window.location.replace(firstPage.route);
                  }
                }
                throw error;
              } finally {
                state.isLoadingOrder = false;
              }
            })()
          )
        )
      )
      .subscribe({
        error: (err) => {
          console.error("Error during order update:", err);
        },
      });
    updateOrderSubject.next();

    // try {
    //   if (order && order._id) {
    //     const resUpdateOrder = await proxy.updateOrder(order._id, data);
    //     response = resUpdateOrder;
    //     if (resUpdateOrder?.data?.doneOrder) {
    //       dispatch("resetOrderAction");
    //       dispatch("clearOnboardingData");
    //       return data;
    //     }
    //   } else {
    //     response = await proxy.createOrder(data);
    //   }
    //   if (response) {
    //     dispatch("setOrderAction", response.data);
    //     Cookies.set("orderId", response.data._id, {
    //       expires: 7,
    //       sameSite: "strict",
    //       secure: true,
    //     });
    //   }
    //   return data;
    // } catch (error) {
    //   if (
    //     error.message &&
    //     error.message.includes("Your session has been done")
    //   ) {
    //     localStorage.clear();
    //     dispatch("resetOrderAction");
    //     dispatch("clearOnboardingData");
    //     const firstPage = getters.getFirstPage(state, getters);
    //     window.location.replace(firstPage.route);
    //   }
    //   throw error;
    // }
  },
  async getCartDataFromApi({ state }) {
    const {
      secretaries,
      hasCompanySecretary,
      shares,
      hasMailroom,
      accountingSubscriptionType,
      accountingAddons,
      accountingAddonsAdditionalData,
      bundleId,
      coupon,
      accountingExpense,
      accountingTier,
      needAccounting,
      inputtedAverageMonthlyExpense,
      annualUpfrontSelected,
      selectedOptionalFlowServices,
      addOnsSelected,
      businessAccountServicesIncluded,
      isTransferAccountingUK,
      isCompletedQuestionnaire,
      questionnaireAnswerList,
      isCheckoutPage,
      isBilledYearly,
    } = state;

    if (!bundleId) return;

    const listBusinessAccountSelected =
      businessAccountServicesIncluded
        .filter((item) => item.selected)
        ?.map((item) => item.service) || [];

    const onboardingData = {
      secretaries,
      hasCompanySecretary,
      shares,
      hasMailroom,
      accountingSubscriptionType,
      accountingAddons,
      accountingAddonsAdditionalData,
      accountingExpense,
      accountingTier,
      coupon,
      needAccounting,
      inputtedAverageMonthlyExpense,
      annualUpfrontSelected,
      selectedOptionalFlowServices,
      addOnsSelected,
      businessAccountServicesIncluded: listBusinessAccountSelected,
      isTransferAccountingUK,
      isCompletedQuestionnaire,
      questionnaireAnswerList,
      isCheckoutPage,
      isBilledYearly,
    };
    getCartObservableInput.next({
      bundleId,
      onboardingData,
      orderId: state.order?._id,
    });
  },
  async onCheckCompanyName({ state }, payload) {
    if (payload) {
      const companyNameExist = await validateCompanyName(state, payload);
      return companyNameExist;
    }
  },
  setOrderAction({ commit }, payload) {
    commit("setOrder", payload);
    commit("setCompanyName", payload.companyName);
    commit("setSuffix", payload.companyNameSuffix);
    commit("setContactDetails", payload.userContactInfo);
    commit("setPointOfContact", payload.pointOfContact);
    commit("setDirectors", payload.directors);
    commit("setSecretaries", payload.secretaries);
    commit("setShares", payload.shares);
    commit("setOwnAddress", payload.ownAddress);
  },
  setAddOnsSelectedAction({ commit, dispatch }, payload) {
    commit("setAddOnsSelected", payload);
    dispatch("getCartDataFromApi");
  },
  setPaymentTypeAction({ commit }, payload) {
    commit("setPaymentType", payload);
  },
  setAllowSleekToStayInTouchAction({ commit }, payload) {
    commit("setAllowSleekToStayInTouch", payload);
  },
  setContactDetailsAction({ commit }, payload) {
    commit("setContactDetails", payload);
  },
  setPointOfContactAction({ commit }, payload) {
    commit("setPointOfContact", payload);
  },
  setDirectorsAction({ commit }, payload) {
    commit("setDirectors", payload);
  },
  setCouponAction({ commit }, payload) {
    commit("setCoupon", payload);
  },
  setCouponArrayAction({ commit }, payload) {
    commit("setCouponArray", payload);
  },
  refetchCartData({ dispatch }) {
    dispatch("getCartDataFromApi");
  },
  setHasCompanySecretaryAction({ dispatch, commit }, payload) {
    commit("setHasCompanySecretary", payload);
    dispatch("getCartDataFromApi");
  },
  // Mailroom
  setHasMailroomAction({ dispatch, commit }, payload) {
    commit("setHasMailroom", payload);
    dispatch("getCartDataFromApi");
  },
  // For the past 2.5 years, nobody knows why we need this but it's here for now...
  // Or IIUC, this is to make the post-onboarding form actually works
  // Contract, ladies!
  setCompanyCreatedIdAction({ commit, state, getters }, companyId) {
    const orderId = state.order?._id ?? companyId;
    console.trace("setCompanyCreatedIdAction", orderId);

    localStorage.setItem(
      "isIncorporationActive",
      getters.isTransfer ? false : true
    );
    commit("setCompanyCreatedId", orderId);
    Cookies.set("createdCompanyId", orderId, { expires: 3 });
    localStorage.setItem("companyId", orderId);
  },
  setInvoiceAction({ commit }, payload) {
    commit("setInvoice", payload);
  },
  // TODO: Just set to initialState, it's fine
  clearOnboardingData({ commit }) {
    commit("resetState");
  },
  setCurrentPageAction({ commit, dispatch }, payload) {
    commit("setCurrentPage", payload.data);
    dispatch("getCartDataFromApi");
  },
  // Accounting
  setAccountingAddonsAction({ commit, dispatch }, payload) {
    commit("setAccountingAddons", payload);
    dispatch("getCartDataFromApi");
  },
  setAccountingAddonsAdditionalDataAction({ commit }, payload) {
    commit("setAccountingAddonsAdditionalData", payload);
  },
  setValidateAccountingAddonsAction({ commit }, payload) {
    commit("setValidateAccountingAddons", payload);
  },
  setNeedAccounting({ commit, dispatch }, payload) {
    commit("setNeedAccounting", payload);
    dispatch("getCartDataFromApi");
  },
  setAccountingExpenseAction({ commit }, payload) {
    commit("setAccountingExpense", payload);
  },
  setAnnualUpfrontSelectedAction({ commit, dispatch }, payload) {
    commit("setAnnualUpfrontSelected", payload);
    dispatch("getCartDataFromApi");
  },
  setIsSelectedAccountingDropdownOptionAction({ commit }, payload) {
    commit("setIsSelectedAccountingDropdownOption", payload);
  },
  setAccountingTierAction({ commit }, payload) {
    commit("setAccountingTier", payload);
  },
  //Business Account
  setBusinessAccountServicesIncludedAction({ commit, dispatch }, payload) {
    commit("setBusinessAccountServicesIncluded", payload);
    dispatch("getCartDataFromApi");
  },
  setInputtedAverageMonthlyExpenseAction({ commit }, payload) {
    commit("setInputtedAverageMonthlyExpense", payload);
  },
  setCheckoutItemsAction({ commit }, payload) {
    commit("setCheckoutItems", payload);
  },
  setDesiredCompanyName({ commit }, payload) {
    commit("setDesiredCompanyName", payload);
  },
  setResumeOnboarding({ commit, dispatch }, payload) {
    commit("setResumeOnboarding", payload);
    dispatch("getCartDataFromApi");
  },

  setPopupTimeout({ commit }, payload) {
    commit("setPopupTimeout", payload);
  },
  setInitialValuesAction({ commit }, payload) {
    commit("setInitialValues", payload);
  },
  setOptionalInclusionAction({ commit, dispatch }) {
    commit("setOptionalInclusion");
    dispatch("getCartDataFromApi");
  },
  setShowComparePlanPopupAction({ commit }, payload) {
    commit("setShowComparePlanPopup", payload);
  },
  addOptionalFlowServiceAction({ commit, dispatch }, payload) {
    commit("addOptionalFlowService", payload);
    dispatch("getCartDataFromApi");
  },
  removeOptionalFlowServiceAction({ commit, dispatch }, payload) {
    commit("removeOptionalFlowService", payload);
    dispatch("getCartDataFromApi");
  },
  setIsPackageMobileAction({ commit }, payload) {
    commit("setIsPackageMobile", payload);
  },
  setIsMandatoryDeclarationCheckedAction({ commit }, payload) {
    commit("setIsMandatoryDeclarationChecked", payload);
  },
  setIsTransferAccountingUKAction({ commit }, payload) {
    commit("setIsTransferAccountingUK", payload);
  },
  setIsAgreeTermsAction({ commit }, payload) {
    commit("setIsAgreeTerms", payload);
  },
  setCollapsedColCAction({ commit }, payload) {
    commit("setCollapsedColC", payload);
  },
  resetOrderAction({ commit }) {
    Cookies.remove("orderId");
    commit("resetOrder");
  },
  setUtmQueryAction({ commit }, payload) {
    commit("setUtmQuery", payload);
  },
  setQuestionnaireAnswerListAction({ commit }, payload) {
    const isCompleted = Object.entries(payload).every(([key, value]) => {
      return key === "vat_registration" ? false : value === false;
    });
    commit("setIsCompletedQuestionnaire", !isCompleted);
    commit("setQuestionnaireAnswerList", payload);
  },
};

const mutations = {
  setCurrentPage(state, payload) {
    state.currentPage = payload;
  },
  setBundleConfiguration(state, payload) {
    state.pageServices = payload.pageServices;
    state.flowServices = get(payload, "flowServices", []);
    state.bundleId = payload.bundleId;
  },
  setOrder(state, payload) {
    state.order = payload;
  },
  setCompanyName(state, payload) {
    state.companyName = payload;
  },
  setSuffix(state, payload) {
    state.suffix = payload;
  },
  setAddOnsSelected(state, payload) {
    state.addOnsSelected = payload;
  },
  setCartData(state, payload) {
    state.cartData = payload;
  },
  setCartLoading(state, payload) {
    state.isCartLoading = payload;
  },
  setPaymentType(state, payload) {
    state.paymentType = payload;
  },
  setAllowSleekToStayInTouch(state, payload) {
    state.isAllowSleekToStayInTouch = payload;
  },
  setContactDetails(state, payload) {
    state.contactDetails = payload;
  },
  setPointOfContact(state, payload) {
    state.pointOfContact = payload;
  },
  setDirectors(state, paylaod) {
    state.directors = paylaod;
  },
  setHasCompanySecretary(state, payload) {
    state.hasCompanySecretary = payload;
  },
  setSecretaries(state, payload) {
    state.secretaries = payload;
  },
  setShares(state, payload) {
    state.shares = payload;
  },
  setCoupon(state, payload) {
    state.coupon = payload;
  },
  setCouponArray(state, payload) {
    state.couponArray = payload;
  },
  setHasMailroom(state, payload) {
    state.hasMailroom = payload;
  },
  setCompanyCreatedId(state, payload) {
    state.companyCreatedId = payload;
  },
  setInvoice(state, payload) {
    state.invoice = payload;
  },
  setAccountingExpense(state, payload) {
    state.accountingExpense = payload;
  },
  setAnnualUpfrontSelected(state, payload) {
    state.annualUpfrontSelected = payload;
  },
  setIsSelectedAccountingDropdownOption(state, payload) {
    state.isSelectedAccountingDropdownOption = payload;
  },
  setAccountingTier(state, payload) {
    state.accountingTier = payload;
  },
  setAccountingSubscriptionType(state, payload) {
    state.accountingSubscriptionType = payload;
  },
  setNeedAccounting(state, payload) {
    state.needAccounting = payload;
  },
  setAccountingAddons(state, payload) {
    state.accountingAddons = payload;
  },
  setAccountingAddonsAdditionalData(state, payload) {
    state.accountingAddonsAdditionalData = payload;
  },
  setValidateAccountingAddons(state, payload) {
    state.validateAccountingAddons = payload;
  },
  setBusinessAccountServicesIncluded(state, payload) {
    state.businessAccountServicesIncluded = payload;
  },
  setInputtedAverageMonthlyExpense(state, payload) {
    state.inputtedAverageMonthlyExpense = payload;
  },
  resetState(state) {
    const stateExcludeCurrentPage = omit(initialState, ["currentPage"]);
    Object.assign(state, stateExcludeCurrentPage);
  },
  setCheckoutItems(state, payload) {
    state.checkoutItems = payload;
  },
  setDesiredCompanyName(state, payload) {
    state.desiredCompanyName = payload;
  },
  setSaveForLater(state, payload) {
    state.saveForLater = payload;
  },
  setResumeOnboarding(state, payload) {
    state.shares = payload.shares;
    state.hasCompanySecretary = payload.hasCompanySecretary;
    state.secretaries = payload.secretaries;
    state.hasMailroom = payload.hasMailroom;
    state.accountingSubscriptionType = payload.accountingSubscriptionType;
    state.accountingAddons = payload.accountingAddons;
    state.bundleId = payload.bundleId;
    // state.coupon = payload.coupon;
    state.accountingExpense = payload.accountingExpense;
    state.annualUpfrontSelected = payload.annualUpfrontSelected;
    state.accountingTier = payload.accountingTier;
    state.needAccounting = payload.needAccounting;
    state.inputtedAverageMonthlyExpense = payload.inputtedAverageMonthlyExpense;
    state.addOnsSelected = payload.addOnsSelected;
  },

  setNeedGuidancePopup(state, payload) {
    state.needGuidancePopup = payload;
  },
  setPopupTimeout(state, payload) {
    state.popupTimeout = payload;
  },
  setInitialValues(state, payload) {
    if (state.isSetInitialValues === false) {
      if (payload) {
        state = Object.assign(state, payload);
        if (
          typeof payload.accountingExpense == "undefined" &&
          !state.accountingExpense
        ) {
          state.accountingExpense = null;
        }
      }

      state.isSetInitialValues = true;
    }
  },
  setOptionalInclusion(state) {
    if (state.isSetOptionalInclusion === true) return;
    // Set preselected optional flow services
    const preselectedFlowServices = state.flowServices
      .filter((x) => x.optional === true && x.preselected === true)
      .map((x) => x.internalName);
    state.selectedOptionalFlowServices = preselectedFlowServices;
    state.isSetOptionalInclusion = true;
  },
  setShowComparePlanPopup(state, payload) {
    state.showComparePlanPopup = payload;
  },
  // Mailroom own Address
  setOwnAddress(state, payload) {
    state.ownAddress = payload || {};
  },
  addOptionalFlowService(state, payload) {
    state.selectedOptionalFlowServices.push(payload);
  },
  removeOptionalFlowService(state, payload) {
    let index = state.selectedOptionalFlowServices.indexOf(payload);
    if (index > -1) {
      state.selectedOptionalFlowServices.splice(index, 1);
    }
  },
  setPackageType(state, payload) {
    state.packageType = payload;
  },
  setIsPackageMobile(state, payload) {
    state.isPackageMobile = payload;
  },
  setIsMandatoryDeclarationChecked(state, payload) {
    state.isMandatoryDeclarationChecked = payload;
  },
  setIsTransferAccountingUK(state, payload) {
    state.isTransferAccountingUK = payload;
  },
  setIsAgreeTerms(state, payload) {
    state.isAgreeTerms = payload;
  },
  setCollapsedColC(state, payload) {
    state.collapsedColC = payload;
  },
  resetOrder(state) {
    state.order = null;
  },
  setUtmQuery(state, payload) {
    state = Object.assign(state, payload);
  },
  setQuestionnaireAnswerList(state, payload) {
    state.questionnaireAnswerList = payload;
  },
  setIsCompletedQuestionnaire(state, payload) {
    state.isCompletedQuestionnaire = payload;
  },
  setCouponEligible(state, payload) {
    state.couponEligible = payload;
  },
  setIsLoadingOrder(state, payload) {
    state.isLoadingOrder = payload;
  },
  setIsCheckoutPage(state, payload) {
    state.isCheckoutPage = payload;
  },
  setIsBilledYearly(state, payload) {
    state.isBilledYearly = payload;
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
